<template>
    <div class="editables-wrapper">
        <div v-if="scrollable" class="editable-widgets">
            <div class="editable-widgets__inner">
                <div class="editable-widgets__inner-content">
                    <template v-for="editable in editablesWithFilteredGroups">
                        <component
                            :is="getComponentName(editable)"
                            v-if="userCanSeeEditable(editable.restricted)"
                            :key="componentKey(editable._id)"
                            :all-expanded="allExpanded"
                            :editable="editable"
                            :groups-affecting-editable="groupsAffectingEditable"
                            :read-only="isReadOnly(editable)"
                            :class="{ 'last-interaction': isLastInteracted(editable._id) }"
                            :show-restriction-control="!sandboxMode"
                            @click="onClick(editable._id)"
                            @remove="onRemove"
                            @update="onUpdate"
                        ></component>
                    </template>

                    <hox-alert
                        v-if="!editablesWithFilteredGroups.length"
                        class="editable-widgets__no-matches"
                        type="warning"
                        size="small"
                        align-text="center"
                        theme="dark"
                    >
                        <template #content>
                            <p>There are no fields matching the filter</p>
                        </template>
                    </hox-alert>
                </div>
            </div>
        </div>
        <div v-else class="editable-widgets">
            <div class="editable-widgets__inner">
                <div class="editable-widgets__inner-content">
                    <template v-for="editable in editablesWithFilteredGroups">
                        <component
                            :is="getComponentName(editable)"
                            v-if="userCanSeeEditable(editable.restricted)"
                            :key="componentKey(editable._id)"
                            :all-expanded="allExpanded"
                            :editable="editable"
                            :groups-affecting-editable="groupsAffectingEditable"
                            :read-only="isReadOnly(editable)"
                            :class="{ 'last-interaction': isLastInteracted(editable._id) }"
                            :show-restriction-control="!sandboxMode"
                            @click="onClick(editable._id)"
                            @remove="onRemove"
                            @update="onUpdate"
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { EditableType } from "shared-utils/enums/editables";
import { TemplateType } from "shared-utils/enums/masterTemplate";
import { USER_ACTIONS } from "shared-utils/enums/user";
import type { Editable, Font, EditableTypeType } from "shared-types";

import { EditorAction, EditorGetters } from "@/store/modules/editor";
import { EditableEvent, EditableTypeWidgetOverrides } from "@/enums/editables.ts";

import EditableTextWidget from "@/components/Campaign/widgets/EditableTextWidget.vue";
import EditableImageWidget from "@/components/Campaign/widgets/EditableImageWidget.vue";
import EditableFileWidget from "@/components/Campaign/widgets/EditableFileWidget.vue";
import EditableArrayWidget from "@/components/Campaign/widgets/EditableArrayWidget.vue";
import EditableBooleanWidget from "@/components/Campaign/widgets/EditableBooleanWidget.vue";
import EditableFolderWidget from "@/components/Campaign/widgets/EditableFolderWidget.vue";
import EditableUrlWidget from "@/components/Campaign/widgets/EditableUrlWidget.vue";
import EditableVideoWidget from "@/components/Campaign/widgets/EditableVideoWidget.vue";
import EditableCssWidget from "@/components/Campaign/widgets/EditableCssWidget.vue";

import * as editableUtils from "@/utils/editables";
import type { UpdatedEditable, UpdatedOverwrite } from "@/types/types";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "EditablesList",
    components: {
        EditableVideoWidget,
        EditableFolderWidget,
        EditableBooleanWidget,
        EditableArrayWidget,
        EditableImageWidget,
        EditableTextWidget,
        EditableFileWidget,
        EditableUrlWidget,
        EditableCssWidget
    },

    props: {
        allExpanded: {
            type: Boolean,
            default: true
        },
        searchValue: {
            type: String,
            default: ""
        },
        scrollable: {
            type: Boolean,
            default: true
        }
    },

    emits: [EditableEvent.Update, EditableEvent.Remove],

    data() {
        return {
            lastInteracted: null as string | null
        };
    },

    computed: {
        availableFontNames() {
            if (!this.fontsByName) {
                return [];
            }

            return Object.keys(this.fontsByName);
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        editables() {
            return this.selectedEditables.filter(e => e.label.toLowerCase().includes(this.searchValue.toLowerCase()));
        },

        editablesWithFilteredGroups() {
            const selectedGroup = this.$store.state.editor.editingGroupName!;

            return this.editables.reduce<Editable[]>((acc, editable) => {
                const defaultValues = editable.defaultValues.filter(({ editableGroups }) =>
                    editableGroups?.includes(selectedGroup)
                );
                if (defaultValues.length) {
                    acc.push({
                        ...editable,
                        defaultValues
                    });
                }

                return acc;
            }, []);
        },

        editingGroupValueId() {
            return (this.$store.getters[EditorGetters.editingGroupValue]?.groupValueId as string) || "";
        },

        fontsByName() {
            return this.$store.getters[EditorGetters.fontsByName] as Record<string, Font>;
        },

        groupsAffectingEditable() {
            return this.editablesWithFilteredGroups.length
                ? this.editablesWithFilteredGroups[0].defaultValues[0].editableGroups
                : [];
        },

        isCampaignPSD() {
            return this.$store.state.campaign.campaignType === TemplateType.PSD;
        },

        selectedEditables() {
            return this.$store.getters[EditorGetters.selectedEditables] as Editable[];
        },

        sandboxMode() {
            return this.$store.state.editor.isSandboxMode;
        }
    },

    watch: {
        editingGroupValueId() {
            this.$store.dispatch(EditorAction.SetEditedEditable, null);
        },

        selectedEditables() {
            this.$store.dispatch(EditorAction.SetEditedEditable, null);
        }
    },

    created() {
        if (this.editables.length) {
            this.lastInteracted = this.editables[0]._id;
        }
    },

    methods: {
        userCanSeeEditable(isRestricted: boolean | undefined) {
            return (
                !isRestricted ||
                userCan(USER_ACTIONS.canManageRestrictedEditables, {
                    campaignId: this.campaignId,
                    clientId: this.clientId
                })
            );
        },

        editablesMissingFonts(editable: Editable) {
            return editableUtils.findMissingFonts(editable, this.availableFontNames);
        },

        getComponentName(editable: Editable) {
            const editableType = editable.type.toLowerCase() as EditableTypeType;

            const hasType = Object.values(EditableType)
                .map(type => type.toLowerCase())
                .includes(editableType);

            if (hasType) {
                const editablesTypes = EditableTypeWidgetOverrides as Record<string, string>;

                return `editable-${editablesTypes[editableType] || editableType}-widget`;
            }

            return "editable-text-widget";
        },

        hasPsdWithMissingFont(editable: Editable) {
            return !!this.editablesMissingFonts(editable).length;
        },

        isLastInteracted(editableId: string) {
            return this.lastInteracted === editableId;
        },

        isReadOnly(editable: Editable) {
            return this.isCampaignPSD && this.hasPsdWithMissingFont(editable);
        },

        onClick(editableId: string) {
            if (this.lastInteracted !== editableId) {
                this.lastInteracted = editableId;
            }
        },

        onRemove(overwriteToRemove: UpdatedOverwrite) {
            this.$emit(EditableEvent.Remove, overwriteToRemove);
        },

        onUpdate(editableUpdate: UpdatedEditable) {
            this.$emit(EditableEvent.Update, editableUpdate);
        },

        componentKey(editableId: string) {
            if (!this.editingGroupValueId) {
                return editableId;
            }

            return `${this.editingGroupValueId}-${editableId}`;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.attached-panel {
    .editable-widgets {
        height: 100vh;
    }
}

.detached-panel {
    .editable-widgets {
        min-height: 80px;
    }

    &.editables-wrapper {
        height: 100%;
        overflow: hidden auto;
    }
}

.editable-widgets {
    height: 100%;

    .expand-editables {
        font-size: 12px;
        position: absolute;
        top: -30px;
        left: 8px;
    }

    &__no-matches {
        margin: $spacing-large;
        color: $grey5;
    }

    .last-interaction.ivu-collapse > .ivu-collapse-item {
        &.ivu-collapse-item-active {
            .ivu-collapse-header {
                background: $cmp-dark-active;
            }
        }
    }

    &--with-scrollbar {
        .editable-widgets__inner-content {
            width: calc(100% - 15px);
            margin-bottom: 75px;
        }
    }

    .editable-widget__body {
        position: relative;

        &--readonly {
            background: $cmp-dark-bg-color;
            opacity: 0.6;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 5;
        }
    }
}
</style>
